import { useTranslation } from 'react-i18next';
import type { Loop } from '@/common/api/types/loops';

interface LoopStampProps {
  loop: Loop;
  onSwitch: (loopId: number) => void;
}

/**
 * Which loop a finding came from — required only in the All loops reading, where findings from
 * several loops sit adjacent in one list and are otherwise indistinguishable. A single-loop reading
 * never renders this: every finding on screen is already known to be that loop's, stated once in the
 * context line above rather than repeated on every row.
 *
 * Styled like the objective name it sits beside in the meta line (`ObjectiveLink`'s dotted
 * underline) rather than as its own accent-coloured token: it inherits the line's secondary colour
 * and sets none of its own, and carries no leading dot — the meta line's own `·` separators between
 * entries already do that job.
 *
 * `uppercase` is set explicitly here rather than relied on from `MetaLine`'s own `uppercase` (part of
 * `TYPE.meta`): a `<button>` does not inherit `text-transform` from an ancestor the way a link or
 * plain span does, so without this the loop name alone would render mixed-case beside every other
 * uppercase entry on the line. `ObjectiveLink` needs no such override because it is a `<span
 * role="button">`, not a real button element.
 *
 * Clickable: reading a finding and wanting to see the rest of that loop's briefing is the obvious
 * next step, and it is the same switch the loop switcher above offers.
 */
export const LoopStamp = ({ loop, onSwitch }: LoopStampProps) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      data-testid="agentic-x-finding-loop-stamp"
      onClick={(event) => {
        event.stopPropagation();
        onSwitch(loop.id);
      }}
      onKeyDown={(event) => {
        // The row this sits inside handles Enter/Space itself (to open the finding) in its own
        // `onKeyDown`, not its `onClick` — stopping propagation only there, as `onClick` already
        // does, would still let a keyboard activation bubble up and toggle the row open at the same
        // time as switching loops.
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
      className="uppercase underline decoration-dotted underline-offset-4 hover:text-text-brand-primary hover:decoration-solid"
    >
      {t(($) => $.loopAgenticInsights.experimental.loopSwitcher.stampLabel, { loop: loop.name })}
    </button>
  );
};
